import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import api from '../services/api';
import { Modal, Button, ListGroup, Table } from 'react-bootstrap';
import InternalRfqDiscussion from './InternalRfqDiscussion';

// --- Icon Components ---
const InfoIcon = ({ className = "w-6 h-6 mr-3 text-indigo-500" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12v-.008z" />
    </svg>
);
const UsersIcon = ({ className = "w-6 h-6 mr-3 text-indigo-500" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m-7.5-2.962a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5zM3.75 18.72v-2.172c0-.962.784-1.75 1.75-1.75h6.5c.966 0 1.75.788 1.75 1.75v2.172A9.094 9.094 0 0112 18.75a9.094 9.094 0 01-8.25-2.028z" />
    </svg>
);
const ChartBarIcon = ({ className = "w-5 h-5 mr-2" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
);
const AwardIcon = ({ className = "w-5 h-5" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" clipRule="evenodd" />
    </svg>
);

// --- UI Sub-components ---
const LoadingIndicator = ({ text = "Loading RFQ details..." }) => (
    <div className="min-h-screen flex items-center justify-center bg-gray-100"><div className="text-center text-gray-600">{text}</div></div>
);

const RfqInfoCard = ({ rfq }) => (
    <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 h-fit">
        <div className="flex items-center mb-4"> <InfoIcon /> <h2 className="text-xl font-bold text-gray-800">RFQ Details</h2> </div>
        <ListGroup variant="flush">
            <ListGroup.Item><strong>RFQ ID:</strong> #{rfq.id} - {rfq.name}</ListGroup.Item>
            <ListGroup.Item><strong>Description:</strong> {rfq.short_description || 'N/A'}</ListGroup.Item>
            <ListGroup.Item><strong>Countries:</strong> {rfq.countries?.join(', ') || 'N/A'}</ListGroup.Item>
            {rfq.countries?.includes('UAE') && <ListGroup.Item><strong>UAE Cities:</strong> {rfq.cities_uae?.join(', ') || 'N/A'}</ListGroup.Item>}
            <ListGroup.Item><strong>Methodologies:</strong> {rfq.methodologies?.join(', ') || 'N/A'}</ListGroup.Item>
            <ListGroup.Item><strong>Data Collection:</strong> {rfq.data_collection_methods?.join(', ') || 'N/A'}</ListGroup.Item>
            <ListGroup.Item><strong>Target Segment:</strong> {rfq.target_segment || 'N/A'}</ListGroup.Item>
            <ListGroup.Item><strong>Timeline:</strong> {rfq.project_timeline || 'N/A'}</ListGroup.Item>
            <ListGroup.Item><strong>Created By:</strong> {rfq.created_by_username || 'System'}</ListGroup.Item>
            <ListGroup.Item><strong>Created At:</strong> {new Date(rfq.created_at).toLocaleDateString()}</ListGroup.Item>
        </ListGroup>
    </div>
);

const EligibleVendorItem = ({ vendor, isSelected, onSelect }) => (
    <div className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded-lg border border-gray-200 transition-colors">
        <div className="flex items-center">
            <input type="checkbox" id={`vendor-${vendor.id}`} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer" checked={isSelected} onChange={() => onSelect(vendor.id)} />
            <label htmlFor={`vendor-${vendor.id}`} className="ml-3 text-gray-800 cursor-pointer">
                <Link to={`/vendors/${vendor.id}`} className="font-medium hover:underline text-indigo-600">{vendor.name}</Link>
                <span className="text-sm text-gray-500 ml-2">({vendor.countries?.join(', ') || vendor.country || 'N/A'})</span>
            </label>
        </div>
    </div>
);

const ResponseDetailModal = ({ show, onHide, invitation }) => {
    if (!show || !invitation) return null;
    const formatCurrency = (value) => { const num = parseFloat(value); return !isNaN(num) ? `$${num.toFixed(2)}` : 'N/A'; };
    const overallCost = invitation.overall_cost ?? invitation.response_data?.overall_cost;
    const otherCosts = invitation.other_costs ?? invitation.response_data?.other_costs;
    const timeline = invitation.vendor_timeline || invitation.response_data?.vendor_timeline;
    const alternatives = invitation.alternative_options || invitation.response_data?.alternative_options;
    const considerations = invitation.considerations || invitation.response_data?.considerations;
    const feasibility = invitation.feasibility_responses ?? invitation.response_data?.feasibility_responses;
    const cpi = invitation.cpi_responses ?? invitation.response_data?.cpi_responses;

    return (
        <Modal show={show} onHide={onHide} size="lg" centered scrollable>
            <Modal.Header closeButton><Modal.Title>Response from {invitation.vendor?.name || 'Unknown'}</Modal.Title></Modal.Header>
            <Modal.Body>
                <ListGroup variant="flush" className="mb-4">
                    <ListGroup.Item className="d-flex justify-content-between align-items-center bg-light"><strong>Overall Quoted Cost</strong><strong className="text-success h5 mb-0">{formatCurrency(overallCost)}</strong></ListGroup.Item>
                    <ListGroup.Item className="d-flex justify-content-between align-items-center"><strong>Other Costs</strong><span>{formatCurrency(otherCosts)}</span></ListGroup.Item>
                    <ListGroup.Item className="d-flex justify-content-between align-items-center"><strong>Proposed Timeline</strong><span>{timeline || 'N/A'}</span></ListGroup.Item>
                    <ListGroup.Item><div className="d-flex flex-column"><strong>Alternative Options</strong><p className="text-muted mb-0 mt-1">{alternatives || 'N/A'}</p></div></ListGroup.Item>
                    <ListGroup.Item><div className="d-flex flex-column"><strong>Considerations</strong><p className="text-muted mb-0 mt-1">{considerations || 'N/A'}</p></div></ListGroup.Item>
                </ListGroup>
                <h5>Feasibility & CPI Breakdown</h5>
                <Table striped bordered hover responsive size="sm">
                    <thead><tr><th>Method</th><th>Feasibility</th><th>CPI</th></tr></thead>
                    <tbody>
                        {Object.entries(feasibility || {}).map(([method, feasibleStatus]) => (<tr key={method}><td>{method}</td><td>{feasibleStatus}</td><td>{formatCurrency(cpi?.[method])}</td></tr>))}
                        {Object.keys(feasibility || {}).length === 0 && (<tr><td colSpan="3" className="text-center text-muted">No breakdown provided.</td></tr>)}
                    </tbody>
                </Table>
            </Modal.Body>
            <Modal.Footer><Button variant="secondary" onClick={onHide}>Close</Button></Modal.Footer>
        </Modal>
    );
};

const InvitedVendorItem = ({ invitation, rfqId, onAwardSuccess }) => {
    const [copied, setCopied] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isAwarding, setIsAwarding] = useState(false);
    
    const getInvitationLink = (token) => `${process.env.REACT_APP_FRONTEND_URL || window.location.origin}/rfqs/public/invitation/${token}/`;

    const handleCopy = () => {
        navigator.clipboard.writeText(getInvitationLink(invitation.invitation_token));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleAward = async () => {
        const notes = prompt("Enter any award notes (optional):");
        if (notes === null) return;

        setIsAwarding(true);
        try {
            await api.post(`/rfqs/${rfqId}/award/`, {
                invitation_id: invitation.id,
                award_notes: notes,
            });
            alert(`RFQ successfully awarded to ${invitation.vendor.name}!`);
            onAwardSuccess();
        } catch (error) {
            console.error("Failed to award RFQ:", error);
            alert(`Error: ${error.response?.data?.error || "Could not award RFQ."}`);
        } finally {
            setIsAwarding(false);
        }
    };
    
    const statusClasses = {'PENDING': 'bg-gray-100 text-gray-800', 'VIEWED': 'bg-yellow-100 text-yellow-800', 'SUBMITTED': 'bg-green-100 text-green-800'};
    const statusClass = statusClasses[invitation.status] || 'bg-gray-100 text-gray-800';

    return (
        <>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center">
                    <div>
                        <p className="text-gray-800 font-semibold">{invitation.vendor?.name || 'Unknown Vendor'}</p>
                        <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${statusClass}`}>{invitation.status}</span>
                    </div>
                    {invitation.status === 'SUBMITTED' ? (
                        <div className="flex items-center space-x-2">
                             <Button variant="outline-primary" size="sm" onClick={() => setShowModal(true)}>View Response</Button>
                             <Button variant="success" size="sm" onClick={handleAward} disabled={isAwarding} className="d-flex align-items-center">
                                <AwardIcon className="w-4 h-4 mr-1"/>
                                {isAwarding ? 'Awarding...' : 'Award'}
                             </Button>
                        </div>
                    ) : (
                        <div className="flex items-center space-x-2 w-full">
                            <input type="text" readOnly value={getInvitationLink(invitation.invitation_token)} className="flex-grow w-full bg-white text-gray-500 text-xs p-2 rounded-md border border-gray-300"/>
                            <button onClick={handleCopy} className="py-2 px-3 text-xs font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-md transition">{copied ? 'Copied!' : 'Copy'}</button>
                        </div>
                    )}
                </div>
            </div>
            <ResponseDetailModal show={showModal} onHide={() => setShowModal(false)} invitation={invitation} />
        </>
    );
};

// --- MAIN COMPONENT ---
const RfqDetail = () => {
    const { rfqId } = useParams();
    const location = useLocation();
    const discussionRef = useRef(null);

    const [rfq, setRfq] = useState(null);
    const [eligibleVendors, setEligibleVendors] = useState([]);
    const [selectedVendorIds, setSelectedVendorIds] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        setError(null);
        try {
            const rfqRes = await api.get(`/rfqs/${rfqId}/`);
            setRfq(rfqRes.data);
            if (rfqRes.data.status !== 'AWARDED') {
                const eligibleRes = await api.get(`/rfqs/${rfqId}/eligible_vendors/`);
                setEligibleVendors(eligibleRes.data);
            } else {
                setEligibleVendors([]);
            }
        } catch (err) {
            console.error("Error fetching RFQ data:", err);
            setError("Could not load RFQ details. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, [rfqId]);

    useEffect(() => {
        setIsLoading(true);
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (rfq && location.hash === '#discussion' && discussionRef.current) {
            const timer = setTimeout(() => {
                discussionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [rfq, location.hash]);
 const handleVendorSelect = (vendorId) => {
        setSelectedVendorIds(prev => prev.includes(vendorId) ? prev.filter(id => id !== vendorId) : [...prev, vendorId]);
    };
    const handleSendInvitations = async () => {
        if (selectedVendorIds.length === 0) {
            alert("Please select at least one vendor to invite.");
            return;
        }
        try {
            await api.post(`/rfqs/${rfqId}/send_invitations/`, { vendor_ids: selectedVendorIds });
            alert("Invitations sent successfully!");
            setSelectedVendorIds([]);
            fetchData();
        } catch (err) {
            console.error("Error sending invitations:", err);
            alert(`Failed to send invitations. ${err.response?.data?.error || 'Please try again.'}`);
        }
    };

    const handleAwardSuccess = () => {
        setIsLoading(true);
        fetchData();
    };

    if (isLoading && !rfq) return <LoadingIndicator />;
    if (error && !rfq) return <div className="min-h-screen flex items-center justify-center bg-gray-100"><p className="text-red-600">{error}</p></div>;
    if (!rfq) return <div className="min-h-screen flex items-center justify-center bg-gray-100"><p>RFQ not found.</p></div>;

    const invitedVendorsList = rfq.invitations || [];
    const submittedResponsesCount = invitedVendorsList.filter(inv => inv.status === 'SUBMITTED').length;

    return (
        <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
            <div className="max-w-6xl mx-auto">
                <header className="mb-8 flex justify-between items-center">
                    <div>
                        <Link to="/vendors/rfqs" className="text-sm text-indigo-600 hover:underline mb-2 inline-block">← Back to RFQ List</Link>
                        <h1 className="text-3xl font-extrabold text-gray-900">RFQ Management</h1>
                        <p className="mt-1 text-lg text-gray-600">Details for RFQ #{rfq.id} - {rfq.name}</p>
                    </div>
                    {submittedResponsesCount > 0 && rfq.status !== 'AWARDED' && (
                        <Link to={`/rfqs/${rfq.id}/responses-summary`} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700">
                            <ChartBarIcon /> Compare Responses ({submittedResponsesCount})
                        </Link>
                    )}
                </header>
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                    <div className="lg:col-span-1 lg:sticky lg:top-8"><RfqInfoCard rfq={rfq} /></div>
                    
                    <div className="lg:col-span-2">
                        {rfq.status !== 'AWARDED' ? (
                            <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
                                <div className="flex items-center mb-6"> <UsersIcon /> <h2 className="text-xl font-bold text-gray-800">Vendor Management</h2> </div>
                                
                                <section className="mb-8">
                                    <h3 className="text-lg font-semibold text-gray-700 mb-3">Eligible Vendors ({eligibleVendors.length})</h3>
                                    {eligibleVendors.length > 0 ? (
                                        <div className="space-y-3 max-h-72 overflow-y-auto pr-2">
                                            {eligibleVendors.map(vendor => (
                                                <EligibleVendorItem key={vendor.id} vendor={vendor} isSelected={selectedVendorIds.includes(vendor.id)} onSelect={handleVendorSelect} />
                                            ))}
                                        </div>
                                    ) : (<p className="text-gray-500 italic">No new eligible vendors found for this RFQ's criteria.</p>)}
                                     <button onClick={handleSendInvitations} disabled={isLoading || selectedVendorIds.length === 0} className="mt-4 inline-flex items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
                                        Send Invitations ({selectedVendorIds.length})
                                    </button>
                                </section>

                                <hr className="my-8 border-gray-200" />

                                <section>
                                     <h3 className="text-lg font-semibold text-gray-700 mb-3">Invited Vendors ({invitedVendorsList.length})</h3>
                                     {invitedVendorsList.length > 0 ? (
                                        <div className="space-y-3">
                                            {invitedVendorsList.map(inv => <InvitedVendorItem key={inv.id} invitation={inv} rfqId={rfq.id} onAwardSuccess={handleAwardSuccess} />)}
                                        </div>
                                     ) : (<p className="text-gray-500 italic">No vendors have been invited yet.</p>)}
                                </section>
                            </div>
                        ) : (
                            <div ref={discussionRef}>
                                <InternalRfqDiscussion rfqId={rfqId} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RfqDetail;